/**
 * GMA-1628 (CompassCare Web 13/14 — Schedule / Activities) — page composition.
 *
 * Per-client schedule screen (docs/design-mockups/CompassCare Schedule.dc.html):
 * the client selector rail (search + severity dot + group-home subtitle), the
 * day agenda (Prev / Today / Next day navigation, timezone chip, four-status
 * legend, status-chipped activity list), and the right rail (selected-day KPI
 * counts + completion progress via the pure summarizeDay selector, the Daily
 * Activity Summary timeline with caregiver attribution, and Run Activity
 * Report handing off to the Reports hub).
 *
 * The status + edit rules come from the pure scheduleModel and are enforced
 * VISIBLY, never re-implemented:
 *   - every chip/KPI derives through deriveActivityStatus (RED reserved for
 *     "missed"; a flagged occurrence reads "Needs follow-up", not missed);
 *   - the drawer's Edit action runs the REAL planSeriesEdit — a past
 *     COMPLETED occurrence is locked per GMA-1611: the lock is shown before
 *     any attempt, and a blocked attempt raises a role="alert" error (same
 *     family-gating pattern as ReportsPage/MessagesPage);
 *   - the Add Activity template catalog is single-sourced from ACTIVITY_TYPES.
 *
 * Layout note (recorded deviation): the demo agenda is a DataTable-style
 * day-list rather than a dark-skinned @event-calendar month grid — the legacy
 * calendar's skin could not be cleanly dark-tokened in one pass; the
 * WebCalendar wrapper remains a follow-up slice (FACELIFT_BUILD_PLAN §GMA-1628
 * slice 4). Timezone display REUSES activityTimezone (no re-implementation).
 *
 * Presentational + fail-safe: all data is injected by the route (testable)
 * and defaults to empty states; null / malformed rows can never break the
 * render. Colors/fonts/severity come from the single design-token set. a11y:
 * every control is a native <button> (aria-pressed for client selection),
 * client/day switches, completions, and template picks are announced through
 * a polite role="status" live region, and status is carried by chip TEXT —
 * never color alone.
 */
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import DetailDrawer from "../../components/web/DetailDrawer";
import {
  ACTIVITY_TYPES,
  activityTypeFor,
  STATUS_SEVERITY,
  deriveActivityStatus,
  planSeriesEdit,
  summarizeDay,
} from "./scheduleModel";
import { severityToken, theme } from "../../styles/designTokens";
import { formatClientIanaTzReadable } from "../../utils/activityTimezone";

// Human chip text for the model's four statuses (mockup copy). Which statuses
// exist — and which one owns RED — stays single-sourced in scheduleModel.
const STATUS_LABELS = Object.freeze({
  completed: "Completed",
  upcoming: "Upcoming",
  missed: "Missed",
  needs_follow_up: "Needs follow-up",
});

// Right-rail KPI order + labels (mockup: Done / Missed / Follow-up / Upcoming).
const STAT_ROWS = Object.freeze([
  Object.freeze({ status: "completed", label: "Done" }),
  Object.freeze({ status: "missed", label: "Missed" }),
  Object.freeze({ status: "needs_follow_up", label: "Follow-up" }),
  Object.freeze({ status: "upcoming", label: "Upcoming" }),
]);

// GMA-1611 lock copy — shown proactively in the drawer AND raised as the
// role="alert" error when a blocked edit is attempted.
const EDIT_LOCKED_COPY =
  "Completed and locked — past completed activities can't be edited or deleted (GMA-1611).";

const isRecord = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const asRows = (v) => (Array.isArray(v) ? v.filter(isRecord) : []);
const asText = (v, fallback = "") =>
  typeof v === "string" && v.trim() !== "" ? v : fallback;

const panelStyle = {
  background: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
};

const panelHeadingStyle = {
  margin: 0,
  padding: "12px 14px 8px",
  fontSize: 12,
  color: theme.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const toolButtonStyle = (enabled = true) => ({
  minHeight: 32,
  padding: "4px 12px",
  border: `1px solid ${theme.border}`,
  borderRadius: 6,
  background: theme.surface,
  color: enabled ? theme.textPrimary : theme.textMuted,
  cursor: enabled ? "pointer" : "not-allowed",
  fontFamily: theme.fontUI,
  fontSize: 12,
});

const monoMutedStyle = {
  fontFamily: theme.fontMono,
  fontSize: 12,
  color: theme.textMuted,
  whiteSpace: "nowrap",
};

/** Status chip: text label + severity tint from the single token set. The
 * neutral "upcoming" status (null severity) renders untinted. */
function StatusChip({ status }) {
  const sev = STATUS_SEVERITY[status];
  const color = sev ? severityToken(sev).color : theme.textMuted;
  return (
    <span
      data-testid="schedule-status-chip"
      data-status={status}
      style={{
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        whiteSpace: "nowrap",
        border: `1px solid ${sev ? color : theme.border}`,
        color: sev ? color : theme.textMuted,
        background: "transparent",
      }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

StatusChip.propTypes = { status: PropTypes.string };

/** The activity's status with the local mark-complete overlay applied. */
const effective = (activity, completions) =>
  completions.has(activity.id) ? { ...activity, completed: true } : activity;

/**
 * GMA-1611 edit plan for one activity through the REAL planSeriesEdit. The
 * activity's own occurrence list (recurring series) is used when present —
 * with the local completion overlay applied to the target — else the activity
 * stands in as a single-occurrence series.
 */
function editPlanFor(activity, completions, nowMs) {
  const self = effective(activity, completions);
  const series = Array.isArray(activity.occurrences)
    ? activity.occurrences.map((occ) =>
        isRecord(occ) && occ.id === activity.id
          ? { ...occ, completed: self.completed === true }
          : occ,
      )
    : [{ id: self.id, startMs: self.startMs, completed: self.completed === true }];
  return planSeriesEdit(series, {
    scope: "this",
    targetId: activity.id,
    nowMs,
  });
}

export default function SchedulePage({
  clients = [],
  days = [],
  defaultDayId,
  now,
  onRunActivityReport,
  onEditActivity,
  onMarkComplete,
}) {
  const clientRows = asRows(clients);
  const dayRows = asRows(days);
  const nowMs = typeof now === "number" && Number.isFinite(now) ? now : Date.now();
  const defaultDayIndex = Math.max(
    0,
    dayRows.findIndex((d) => d.id === defaultDayId),
  );

  const [selectedClientId, setSelectedClientId] = useState(() =>
    clientRows.length > 0 ? clientRows[0].id : null,
  );
  const [dayIndex, setDayIndex] = useState(defaultDayIndex);
  const [clientQuery, setClientQuery] = useState("");
  // Locally marked-complete activity ids (demo overlay; the route re-mounts
  // with fresh data per visit — real persistence is the live-data follow-up).
  const [completions, setCompletions] = useState(() => new Set());
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [editBlocked, setEditBlocked] = useState("");
  const [announcement, setAnnouncement] = useState("");

  const selectedClient =
    clientRows.find((c) => c.id === selectedClientId) || null;
  const day = dayRows[Math.min(dayIndex, Math.max(0, dayRows.length - 1))] || null;

  // The selected client's agenda for the visible day, with the completion
  // overlay applied — ONE list drives the agenda, the KPI rail, and the
  // summary timeline so they can never disagree.
  const agenda = useMemo(() => {
    const rows = day ? asRows(day.activities) : [];
    const scoped = selectedClient
      ? rows.filter((a) => a.clientId === selectedClient.id)
      : rows;
    return scoped.map((a) => effective(a, completions));
  }, [day, selectedClient, completions]);

  const daySummary = summarizeDay(agenda, nowMs);
  const selectedActivity =
    agenda.find((a) => a.id === selectedActivityId) || null;
  const selectedPlan = selectedActivity
    ? editPlanFor(selectedActivity, completions, nowMs)
    : null;
  const selectedLocked =
    selectedActivity !== null &&
    selectedPlan !== null &&
    !selectedPlan.editable.includes(selectedActivity.id);
  const timezoneReadable = selectedClient
    ? formatClientIanaTzReadable(selectedClient.timezone)
    : "";

  const visibleClients = clientRows.filter((c) =>
    asText(c.name).toLowerCase().includes(clientQuery.trim().toLowerCase()),
  );

  const closeDrawer = () => {
    setSelectedActivityId(null);
    setTemplatesOpen(false);
    setEditBlocked("");
  };

  const selectClient = (client) => {
    setSelectedClientId(client.id);
    closeDrawer();
    setAnnouncement(`Viewing schedule for ${asText(client.name, "client")}`);
  };

  const goToDay = (index) => {
    const clamped = Math.min(Math.max(index, 0), Math.max(0, dayRows.length - 1));
    setDayIndex(clamped);
    closeDrawer();
    const target = dayRows[clamped];
    if (target) setAnnouncement(`Showing ${asText(target.label, "day")} schedule`);
  };

  const markComplete = (activity) => {
    setCompletions((prev) => new Set(prev).add(activity.id));
    setAnnouncement(`Marked complete: ${asText(activity.title, "activity")}`);
    if (typeof onMarkComplete === "function") onMarkComplete(activity);
  };

  const attemptEdit = (activity) => {
    // GMA-1611 through the REAL model — never a local re-implementation.
    const plan = editPlanFor(activity, completions, nowMs);
    if (!plan.editable.includes(activity.id)) {
      setEditBlocked(EDIT_LOCKED_COPY);
      return;
    }
    setEditBlocked("");
    setAnnouncement(`Editing ${asText(activity.title, "activity")}`);
    if (typeof onEditActivity === "function") onEditActivity(activity, plan);
  };

  const pickTemplate = (typeKey) => {
    // #413: ACTIVITY_TYPES["constructor"] is truthy and would render
    // Object's shape instead of falling back to the raw key.
    const entry = activityTypeFor(typeKey);
    const label = entry ? entry.label : typeKey;
    setAnnouncement(`Template selected: ${label}`);
    closeDrawer();
  };

  return (
    <div
      data-testid="schedule-page"
      style={{
        display: "grid",
        gridTemplateColumns: "240px minmax(0, 1fr) 280px",
        gap: 16,
        padding: 16,
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
        alignItems: "start",
      }}
    >
      {/* Polite live region: announces client/day switches, completions, and
          template picks so screen-reader users track schedule updates. */}
      <div
        data-testid="schedule-announcement"
        role="status"
        aria-live="polite"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {announcement}
      </div>

      {/* ---- Client selector rail ---- */}
      <section aria-label="Clients" style={panelStyle}>
        <h2 style={panelHeadingStyle}>Clients</h2>
        <div style={{ padding: "0 10px 10px" }}>
          <input
            type="search"
            aria-label="Search clients"
            placeholder={`Search ${clientRows.length} clients`}
            value={clientQuery}
            onChange={(e) => setClientQuery(e.target.value)}
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              padding: "7px 10px",
              color: theme.textPrimary,
              fontFamily: theme.fontUI,
              fontSize: 12,
            }}
          />
        </div>
        {clientRows.length === 0 ? (
          <p
            data-testid="schedule-clients-empty"
            style={{ padding: "0 14px 14px", margin: 0, color: theme.textMuted, fontSize: 13 }}
          >
            No clients available
          </p>
        ) : (
          <ul
            data-testid="schedule-client-list"
            style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 480, overflowY: "auto" }}
          >
            {visibleClients.map((client) => {
              const pressed = selectedClient !== null && client.id === selectedClient.id;
              const sev = severityToken(client.status);
              const name = asText(client.name, "Unknown client");
              const sub = [asText(client.home), asText(client.room)]
                .filter(Boolean)
                .join(" · ");
              return (
                <li key={client.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <button
                    type="button"
                    aria-pressed={pressed}
                    aria-label={`Select client: ${name} (${sev.label})`}
                    onClick={() => selectClient(client)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      textAlign: "left",
                      padding: "9px 14px",
                      border: "none",
                      background: pressed ? theme.bg : "transparent",
                      color: theme.textPrimary,
                      cursor: "pointer",
                      fontFamily: theme.fontUI,
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: sev.color,
                        flex: "none",
                      }}
                    />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13 }}>{name}</span>
                      {sub ? (
                        <span style={{ display: "block", fontSize: 11, color: theme.textMuted }}>
                          {sub}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ---- Day agenda ---- */}
      <section aria-label="Schedule" style={{ ...panelStyle, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            padding: "12px 14px",
            borderBottom: `1px solid ${theme.border}`,
          }}
        >
          <button
            type="button"
            aria-label="Previous day"
            onClick={() => goToDay(dayIndex - 1)}
            disabled={dayIndex <= 0}
            style={toolButtonStyle(dayIndex > 0)}
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => goToDay(defaultDayIndex)}
            style={toolButtonStyle()}
          >
            Today
          </button>
          <button
            type="button"
            aria-label="Next day"
            onClick={() => goToDay(dayIndex + 1)}
            disabled={dayIndex >= dayRows.length - 1}
            style={toolButtonStyle(dayIndex < dayRows.length - 1)}
          >
            ›
          </button>
          <h2
            data-testid="schedule-day-label"
            style={{ margin: 0, fontSize: 14, fontWeight: 700 }}
          >
            {day ? asText(day.label, "Schedule") : "Schedule"}
          </h2>
          <div style={{ flex: 1 }} />
          {timezoneReadable ? (
            <span data-testid="schedule-timezone" style={monoMutedStyle}>
              {timezoneReadable}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setSelectedActivityId(null);
              setEditBlocked("");
              setTemplatesOpen(true);
            }}
            style={toolButtonStyle()}
          >
            Add Activity
          </button>
        </div>

        <div
          data-testid="schedule-legend"
          aria-label="Status legend"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 14,
            padding: "10px 14px",
            borderBottom: `1px solid ${theme.border}`,
          }}
        >
          {STAT_ROWS.map(({ status }) => {
            const sev = STATUS_SEVERITY[status];
            return (
              <span
                key={status}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 11,
                  color: theme.textMuted,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: sev ? severityToken(sev).color : theme.textMuted,
                  }}
                />
                {STATUS_LABELS[status]}
              </span>
            );
          })}
        </div>

        {agenda.length === 0 ? (
          <p
            data-testid="schedule-agenda-empty"
            style={{ padding: 16, margin: 0, color: theme.textMuted }}
          >
            No activities scheduled
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {agenda.map((activity, index) => {
              const status = deriveActivityStatus(activity, nowMs);
              const title = asText(activity.title, "Activity");
              const typeEntry = activityTypeFor(activity.type);
              const open = () => {
                setTemplatesOpen(false);
                setEditBlocked("");
                setSelectedActivityId(activity.id);
              };
              return (
                <li
                  key={activity.id !== null && activity.id !== undefined ? activity.id : index}
                  data-testid="schedule-activity-row"
                  data-status={status}
                  style={{ borderBottom: `1px solid ${theme.border}` }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={`Open activity: ${title}`}
                    onClick={open}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
                        e.preventDefault();
                        open();
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 14px",
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ ...monoMutedStyle, minWidth: 64 }}>
                      {asText(activity.timeLabel, "—")}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13 }}>{title}</span>
                      <span style={{ display: "block", fontSize: 11, color: theme.textMuted }}>
                        {[
                          typeEntry ? typeEntry.label : null,
                          asText(activity.recurrenceLabel),
                          asText(activity.caregiver),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    <StatusChip status={status} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ---- Right rail: day KPIs + summary + report handoff ---- */}
      <div style={{ display: "grid", gap: 16 }}>
        <section aria-label="Day progress" style={panelStyle}>
          <h2 style={panelHeadingStyle}>
            {day ? asText(day.label, "Day") : "Day"} progress
          </h2>
          <div
            data-testid="schedule-day-stats"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 8,
              padding: "0 14px 10px",
            }}
          >
            {STAT_ROWS.map(({ status, label }) => {
              const sev = STATUS_SEVERITY[status];
              return (
                <div key={status} style={{ textAlign: "center" }}>
                  <div
                    data-testid={`schedule-stat-${status}`}
                    style={{
                      fontFamily: theme.fontMono,
                      fontSize: 16,
                      fontWeight: 600,
                      color: sev ? severityToken(sev).color : theme.textPrimary,
                    }}
                  >
                    {daySummary.counts[status]}
                  </div>
                  <div
                    style={{
                      fontSize: 9,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: theme.textMuted,
                    }}
                  >
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
          <p
            data-testid="schedule-completion"
            style={{ margin: 0, padding: "0 14px 12px", fontSize: 11, color: theme.textMuted }}
          >
            {daySummary.counts.completed} of {daySummary.total} complete
          </p>
        </section>

        <section aria-label="Daily activity summary" style={panelStyle}>
          <h2 style={panelHeadingStyle}>Daily Activity Summary</h2>
          {agenda.length === 0 ? (
            <p style={{ padding: "0 14px 14px", margin: 0, color: theme.textMuted, fontSize: 12 }}>
              No activity recorded
            </p>
          ) : (
            <ul
              data-testid="schedule-day-summary"
              style={{ listStyle: "none", margin: 0, padding: "0 0 6px" }}
            >
              {agenda.map((activity, index) => {
                const status = deriveActivityStatus(activity, nowMs);
                return (
                  <li
                    key={activity.id !== null && activity.id !== undefined ? activity.id : index}
                    data-testid="schedule-summary-row"
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 8,
                      padding: "6px 14px",
                      fontSize: 12,
                    }}
                  >
                    <span style={{ ...monoMutedStyle, minWidth: 56 }}>
                      {asText(activity.timeLabel, "—")}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block" }}>
                        {asText(activity.title, "Activity")}
                      </span>
                      {asText(activity.caregiver) ? (
                        <span style={{ display: "block", fontSize: 11, color: theme.textMuted }}>
                          {activity.caregiver}
                        </span>
                      ) : null}
                    </span>
                    <span style={{ fontSize: 11, color: theme.textMuted, whiteSpace: "nowrap" }}>
                      {STATUS_LABELS[status]}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <button
          type="button"
          onClick={() => {
            if (typeof onRunActivityReport === "function") onRunActivityReport();
          }}
          style={{ ...toolButtonStyle(), minHeight: 38 }}
        >
          Run Activity Report
        </button>
      </div>

      {/* ---- Detail drawer: activity detail OR Add Activity templates ---- */}
      <DetailDrawer
        open={selectedActivity !== null || templatesOpen}
        onClose={closeDrawer}
        title={
          templatesOpen
            ? "Add Activity"
            : selectedActivity
              ? asText(selectedActivity.title, "Activity detail")
              : ""
        }
        type="activity"
      >
        {templatesOpen ? (
          <div data-testid="schedule-templates" style={{ display: "grid", gap: 8 }}>
            <p style={{ margin: 0, fontSize: 12, color: theme.textMuted }}>
              Activity Templates — pick a type to prefill the new activity.
              Saving wires to the live activity form in the real-data follow-up.
            </p>
            {Object.entries(ACTIVITY_TYPES).map(([key, entry]) => (
              <button
                key={key}
                type="button"
                data-testid="schedule-template-option"
                onClick={() => pickTemplate(key)}
                style={{ ...toolButtonStyle(), textAlign: "left" }}
              >
                {entry.label}
              </button>
            ))}
          </div>
        ) : selectedActivity ? (
          <div data-testid="schedule-activity-detail" style={{ display: "grid", gap: 14 }}>
            {/* MUI's modal Drawer aria-hides everything outside it — including
                the page-level live region — so in-drawer actions (Editing /
                Marked complete) are announced from THIS region while the
                drawer is open. */}
            <div
              data-testid="schedule-drawer-announcement"
              role="status"
              aria-live="polite"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                margin: -1,
                padding: 0,
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              {announcement}
            </div>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <StatusChip status={deriveActivityStatus(selectedActivity, nowMs)} />
              <span style={monoMutedStyle}>{asText(selectedActivity.timeLabel, "—")}</span>
              {asText(selectedActivity.recurrenceLabel) ? (
                <span style={{ fontSize: 12, color: theme.textMuted }}>
                  {selectedActivity.recurrenceLabel}
                </span>
              ) : null}
            </div>
            {asText(selectedActivity.caregiver) ? (
              <p style={{ margin: 0, fontSize: 13, color: theme.textMuted }}>
                Caregiver: {selectedActivity.caregiver}
              </p>
            ) : null}

            <section aria-label="Notes">
              <h3 style={{ ...panelHeadingStyle, padding: "0 0 4px" }}>Notes</h3>
              <p style={{ margin: 0, fontSize: 13 }}>
                {asText(selectedActivity.notes, "No notes recorded.")}
              </p>
            </section>
            {asText(selectedActivity.prefs) ? (
              <section aria-label="Client Preferences">
                <h3 style={{ ...panelHeadingStyle, padding: "0 0 4px" }}>
                  Client Preferences
                </h3>
                <p style={{ margin: 0, fontSize: 13 }}>{selectedActivity.prefs}</p>
              </section>
            ) : null}

            {selectedLocked ? (
              <p
                data-testid="schedule-edit-guard"
                style={{ margin: 0, fontSize: 12, color: theme.textMuted }}
              >
                {EDIT_LOCKED_COPY}
              </p>
            ) : null}
            {editBlocked ? (
              <p
                data-testid="schedule-edit-blocked"
                role="alert"
                style={{ margin: 0, fontSize: 12, color: severityToken("critical").color }}
              >
                {editBlocked}
              </p>
            ) : null}

            <div
              role="group"
              aria-label="Activity actions"
              style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
            >
              {deriveActivityStatus(selectedActivity, nowMs) !== "completed" ? (
                <button
                  type="button"
                  onClick={() => markComplete(selectedActivity)}
                  style={toolButtonStyle()}
                >
                  Mark complete
                </button>
              ) : null}
              <button
                type="button"
                aria-disabled={selectedLocked ? "true" : "false"}
                aria-label="Edit activity"
                onClick={() => attemptEdit(selectedActivity)}
                style={toolButtonStyle(!selectedLocked)}
              >
                Edit
              </button>
            </div>
          </div>
        ) : null}
      </DetailDrawer>
    </div>
  );
}

SchedulePage.propTypes = {
  /**
   * Selector clients; { id, name, home, room, status, timezone }. Typed as a
   * plain array (not arrayOf(shape)) because the page is intentionally
   * fail-safe for null / malformed rows, and a strict element type would warn
   * for those supported inputs.
   */
  clients: PropTypes.array,
  /** Day buckets; { id, label, activities: [...] } — see demoData.getDemoSchedule. */
  days: PropTypes.array,
  /** Which day id opens first (and where the Today button returns to). */
  defaultDayId: PropTypes.string,
  /** Reference time for status derivation; defaults to Date.now(). */
  now: PropTypes.number,
  /** Handoff to the Reports hub (GMA-1626) — the route wires navigation. */
  onRunActivityReport: PropTypes.func,
  /** Called as onEditActivity(activity, plan) when an edit is PERMITTED. */
  onEditActivity: PropTypes.func,
  /** Called as onMarkComplete(activity) after a local completion applies. */
  onMarkComplete: PropTypes.func,
};
